import React, { useState, useEffect } from 'react';
import { loadRemote } from '@module-federation/runtime';
import { MfWidgetRegistryContext, type WidgetRegistry } from '@brandclub/common-ui';

let validWidgetIds: Set<string> | null = null;
try {
  loadRemote<{ WIDGET_IDS: readonly string[] }>('concierge_widgets/Manifest')
    .then((mod) => {
      if (mod?.WIDGET_IDS) validWidgetIds = new Set(mod.WIDGET_IDS);
    })
    .catch(() => {});
} catch {}

function kebabToPascal(kebab: string): string {
  return kebab.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
}

const MF_RETRY_COUNT = 3;
const MF_RETRY_DELAY = 1500;

function loadWithRetry(pascalName: string, attempt = 1): Promise<{ default: React.ComponentType<any> }> {
  return loadRemote<{ default: React.ComponentType<any> }>(`concierge_widgets/${pascalName}`).then((mod) => {
    if (!mod) throw new Error(`Failed to load concierge_widgets/${pascalName}`);
    return mod;
  }).catch((err) => {
    if (attempt < MF_RETRY_COUNT) {
      return new Promise((resolve) => setTimeout(resolve, MF_RETRY_DELAY)).then(() =>
        loadWithRetry(pascalName, attempt + 1),
      );
    }
    throw err;
  });
}

interface WidgetSlot {
  Component: React.ComponentType<any> | null;
  promise: Promise<void>;
  error: Error | null;
  listeners: Set<() => void>;
}

const widgetSlots = new Map<string, WidgetSlot>();

const LOG = '[MfWidgetRegistry]';

function getWidgetSlot(widgetId: string): WidgetSlot {
  let slot = widgetSlots.get(widgetId);
  if (slot) return slot;

  const pascalName = kebabToPascal(widgetId);
  console.info(LOG, `Loading "${widgetId}" (${pascalName})…`);
  const t0 = performance.now();
  const newSlot: WidgetSlot = { Component: null, promise: null!, error: null, listeners: new Set() };
  newSlot.promise = loadWithRetry(pascalName)
    .then((mod) => {
      newSlot.Component = mod.default;
      console.info(LOG, `"${widgetId}" loaded in ${Math.round(performance.now() - t0)}ms, listeners: ${newSlot.listeners.size}`);
    })
    .catch((err) => {
      newSlot.error = err;
      widgetSlots.delete(widgetId);
      console.warn(LOG, `"${widgetId}" failed after ${Math.round(performance.now() - t0)}ms:`, err.message);
    })
    .finally(() => { newSlot.listeners.forEach((fn) => fn()); });
  widgetSlots.set(widgetId, newSlot);
  return newSlot;
}

function createWidgetComponent(widgetId: string): React.ComponentType<any> {
  return function MfWidget(props: any) {
    const [, rerender] = useState(0);
    // Resolve the slot on every render so error-boundary retries (which set the
    // boundary's `hasError` back to false and re-render this tree) pick up a
    // fresh slot. When a prior load failed, `getWidgetSlot` removes the errored
    // slot from the map, so this next call creates a new one that re-attempts
    // `loadRemote`. Without this, the captured errored slot would make the
    // boundary retry loop throw the same cached error every time and give up.
    const slot = getWidgetSlot(widgetId);

    useEffect(() => {
      if (slot.Component || slot.error) {
        rerender((n) => n + 1);
        return;
      }
      const listener = () => rerender((n) => n + 1);
      slot.listeners.add(listener);
      return () => { slot.listeners.delete(listener); };
    }, [slot]);

    if (slot.error) throw slot.error;
    if (!slot.Component) return null;
    return <slot.Component {...props} />;
  };
}

const widgetCache = new Map<string, React.ComponentType<any>>();

const registry: WidgetRegistry = new Proxy({} as WidgetRegistry, {
  get(_target, prop: string) {
    if (typeof prop !== 'string') return undefined;
    if (validWidgetIds && !validWidgetIds.has(prop)) {
      console.warn(LOG, `Unknown widget ID "${prop}". Valid IDs:`, [...validWidgetIds]);
    }
    let cached = widgetCache.get(prop);
    if (!cached) {
      cached = createWidgetComponent(prop);
      widgetCache.set(prop, cached);
    }
    return cached;
  },
  has(_target, prop: string) {
    if (validWidgetIds) return validWidgetIds.has(prop);
    return true;
  },
});

export function MfWidgetRegistryProvider({ children }: { children: React.ReactNode }) {
  return (
    <MfWidgetRegistryContext.Provider value={registry}>
      {children}
    </MfWidgetRegistryContext.Provider>
  );
}
